<template>
  <div class="utm-overlay" @click.self="$emit('close')">
    <div class="utm-modal" role="dialog" aria-modal="true">
      <!-- ============ Header ============ -->
      <header class="utm-header">
        <ScoreCircle v-if="scan" :percent="scan.score" :size="54" />
        <div class="utm-header__main">
          <div class="utm-header__title">
            <a v-if="scan" :href="scan.url" target="_blank" rel="noopener">
              {{ scan.url }}<i class="fas fa-external-link-alt ms-1"></i>
            </a>
            <template v-else><i class="fas fa-vial me-2"></i>ADA test — custom URL</template>
          </div>
          <div v-if="scan" class="utm-header__meta">
            <span
              v-for="e in scan.engines"
              :key="e.engine"
              class="utm-engine"
              :class="e.ok ? 'utm-engine--ok' : 'utm-engine--fail'"
              :title="e.error || 'Scanned OK'"
            >
              <i :class="e.ok ? 'fas fa-check' : 'fas fa-times'" class="me-1"></i>{{ e.engine }}
            </span>
            <span v-if="scan.duration_ms" class="utm-header__time">
              <i class="fas fa-stopwatch me-1"></i>{{ (scan.duration_ms / 1000).toFixed(1) }}s
            </span>
            <span class="utm-header__note">
              <i class="fas fa-database me-1"></i>Not saved — test scan only
            </span>
          </div>
          <div v-else class="utm-header__meta">
            Runs the scanners against any public URL. Nothing is stored.
          </div>
        </div>
        <button type="button" class="utm-close" aria-label="Close" @click="$emit('close')">
          <i class="fas fa-times"></i>
        </button>
      </header>

      <!-- ============ Form (only when the modal is the entry point) ============ -->
      <form v-if="embedForm" class="utm-form" @submit.prevent="run">
        <div class="utm-form__row">
          <input
            ref="urlInput"
            v-model.trim="url"
            type="url"
            class="utm-form__url"
            placeholder="https://example.gov/page"
            :disabled="running"
            required
          />
          <button type="submit" class="utm-form__btn" :disabled="running || !url">
            <i class="fas" :class="running ? 'fa-spinner fa-spin' : 'fa-play'"></i>
            {{ running ? 'Testing…' : 'Test' }}
          </button>
        </div>
        <div class="utm-form__engines">
          <label v-for="opt in engineOptions" :key="opt.value" class="utm-form__engine" :title="opt.hint">
            <input type="checkbox" :value="opt.value" v-model="engines" :disabled="running" />
            <span>{{ opt.label }}</span>
          </label>
          <label
            class="utm-form__engine utm-form__engine--sep"
            title="Hide findings acknowledged globally, the way a real scan would"
          >
            <input type="checkbox" v-model="applyWarnings" :disabled="running" />
            <span>Apply global warnings</span>
          </label>
        </div>
        <div v-if="error" class="utm-form__error"><i class="fas fa-exclamation-circle me-1"></i>{{ error }}</div>
      </form>

      <!-- ============ Loading ============ -->
      <div v-if="running" class="utm-loading">
        <i class="fas fa-spinner fa-spin me-2"></i>
        Scanning {{ url }} — a page with many linked PDFs can take a couple of minutes.
      </div>

      <!-- ============ Nothing run yet ============ -->
      <div v-else-if="!scan" class="utm-empty utm-empty--idle">
        <i class="fas fa-vial me-2"></i>Enter a URL and hit Test.
      </div>

      <template v-else>

      <!-- Engines that failed — say so explicitly, otherwise their findings are
           just silently missing from the lists below. -->
      <div v-if="failedEngines.length" class="utm-alert">
        <i class="fas fa-exclamation-triangle me-1"></i>
        <span v-for="(e, i) in failedEngines" :key="e.engine">
          <strong>{{ e.engine }}</strong> failed: {{ e.error || 'unknown error' }}<span v-if="i < failedEngines.length - 1">; </span>
        </span>
      </div>
      <div v-if="scan.acknowledged_hidden > 0" class="utm-note">
        <i class="fas fa-eye-slash me-1"></i>
        {{ scan.acknowledged_hidden }} finding(s) hidden by global acknowledgments.
      </div>

      <!-- ============ Tabs ============ -->
      <div class="utm-tabs" role="tablist">
        <button
          type="button"
          role="tab"
          class="utm-tab"
          :class="{ 'utm-tab--active': activeTab === 'errors' }"
          @click="activeTab = 'errors'"
        >
          <i class="fas fa-times-circle text-danger me-1"></i>
          Errors
          <span class="utm-tab__badge">{{ errors.length }}</span>
        </button>
        <button
          type="button"
          role="tab"
          class="utm-tab"
          :class="{ 'utm-tab--active': activeTab === 'warnings' }"
          @click="activeTab = 'warnings'"
        >
          <i class="fas fa-exclamation-triangle text-warning me-1"></i>
          Warnings
          <span class="utm-tab__badge">{{ warnings.length }}</span>
        </button>
        <div class="utm-tabs__right">
          <select v-model="engineFilter" class="utm-select">
            <option value="">All engines</option>
            <option v-for="e in foundEngines" :key="e" :value="e">{{ e }}</option>
          </select>
          <div class="utm-search">
            <i class="fas fa-search utm-search__icon"></i>
            <input v-model.trim="search" type="text" class="utm-search__input" placeholder="Search rule or message…" />
            <button v-if="search" type="button" class="utm-search__clear" aria-label="Clear" @click="search = ''">
              <i class="fas fa-times"></i>
            </button>
          </div>
        </div>
      </div>

      <!-- ============ Body ============ -->
      <div class="utm-body">
        <div v-if="!visibleItems.length" class="utm-empty">
          <template v-if="search || engineFilter">
            <i class="fas fa-search me-2"></i>No {{ activeTab }} matching the filter.
          </template>
          <template v-else-if="activeTab === 'errors'">
            <i class="fas fa-check-circle me-2 text-success"></i>No errors. 🎉
          </template>
          <template v-else>
            <i class="fas fa-check-circle me-2 text-success"></i>No warnings.
          </template>
        </div>

        <ul v-else class="utm-issues">
          <li
            v-for="(item, i) in visibleItems"
            :key="activeTab + '-' + i"
            class="utm-issue"
            :class="activeTab === 'errors' ? 'utm-issue--error' : 'utm-issue--warn'"
          >
            <div class="utm-issue__head">
              <span class="utm-issue__engine" :class="'utm-issue__engine--' + (item.engine || '').toLowerCase()">
                {{ item.engine || '?' }}
              </span>
              <span class="utm-issue__type">{{ item.type || '—' }}</span>
              <code v-if="item.rule_id || item.id" class="utm-issue__rule">{{ item.rule_id || item.id }}</code>
              <a
                v-if="item.help_url"
                :href="item.help_url"
                target="_blank"
                rel="noopener"
                class="utm-issue__help"
                title="Rule documentation"
              >
                <i class="fas fa-book"></i>
              </a>
            </div>
            <div class="utm-issue__msg">{{ item.message || item.description || '—' }}</div>
            <ul v-if="detailsOf(item).length" class="utm-issue__details">
              <li v-for="(d, di) in detailsOf(item)" :key="'d-' + di">{{ d }}</li>
            </ul>
            <div v-if="locationOf(item)" class="utm-issue__loc">
              <small class="text-muted">
                <i class="fas fa-crosshairs me-1"></i><code>{{ locationOf(item) }}</code>
              </small>
            </div>
            <div v-if="item.ratio !== undefined && item.foreground" class="utm-issue__contrast">
              ratio <strong>{{ item.ratio }}</strong>
              · fg <span class="utm-swatch" :style="{ background: item.foreground }"></span><code>{{ item.foreground }}</code>
              · bg <span class="utm-swatch" :style="{ background: item.background }"></span><code>{{ item.background }}</code>
            </div>
          </li>
        </ul>

        <div v-if="visibleItems.length < filteredItems.length" class="utm-load-more">
          <button type="button" class="utm-load-more__btn" @click="limit += 25">Load more</button>
          <small class="text-muted">{{ visibleItems.length }} of {{ filteredItems.length }}</small>
        </div>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import ScoreCircle from '@/components/juniper/ada-auditor/ScoreCircle.vue'
import { runAdaTestScan, ADA_TEST_ENGINES, ADA_TEST_DEFAULT_ENGINES } from '@/utils/adaTestScan'

/**
 * Result modal for the ad-hoc "Test a URL" scan.
 *
 * Two ways in:
 *   - `:result` — the caller already ran the scan (Settings → Audits, where the
 *     form lives on the page) and just wants it displayed.
 *   - `embed-form` — the modal IS the entry point (Home shortcut): it renders
 *     the URL field, runs the scan itself, and shows the findings underneath.
 *
 * Read-only either way: the scan is never persisted, so there are no
 * acknowledge actions here (a rule can only be dismissed from a real report,
 * where the client/page scope is known).
 */
export default {
  name: 'UrlTestModal',
  components: { ScoreCircle },
  props: {
    // A /juniper/ada-auditor/test-scan response body, when the caller ran it.
    result: { type: Object, default: null },
    // Render the URL form inside the modal and run the scan here.
    embedForm: { type: Boolean, default: false },
  },
  data() {
    return {
      // Own form state (embedForm only)
      url: '',
      engines: [...ADA_TEST_DEFAULT_ENGINES],
      applyWarnings: false,
      running: false,
      error: null,
      ownResult: null,

      // Result view state
      activeTab: 'errors',
      search: '',
      engineFilter: '',
      limit: 25,
    }
  },
  computed: {
    // Whichever result we are showing — the caller's or our own.
    scan() {
      return this.result || this.ownResult
    },
    engineOptions() {
      return ADA_TEST_ENGINES
    },
    errors() {
      return (this.scan && this.scan.errors) || []
    },
    warnings() {
      return (this.scan && this.scan.warnings) || []
    },
    failedEngines() {
      return ((this.scan && this.scan.engines) || []).filter(e => !e.ok)
    },
    // Engines that actually produced findings — drives the filter dropdown.
    foundEngines() {
      const set = new Set()
      for (const item of [...this.errors, ...this.warnings]) {
        if (item.engine) set.add(item.engine)
      }
      return [...set].sort()
    },
    filteredItems() {
      const q = this.search.toLowerCase()
      return (this.activeTab === 'errors' ? this.errors : this.warnings).filter(item => {
        if (this.engineFilter && item.engine !== this.engineFilter) return false
        if (!q) return true
        const hay = [item.rule_id, item.id, item.message, item.description].filter(Boolean).join(' ').toLowerCase()
        return hay.includes(q)
      })
    },
    visibleItems() {
      return this.filteredItems.slice(0, this.limit)
    },
  },
  watch: {
    activeTab() {
      this.limit = 25
    },
    search() {
      this.limit = 25
    },
    engineFilter() {
      this.limit = 25
    },
  },
  mounted() {
    document.addEventListener('keydown', this.onKeydown)
    if (this.embedForm) {
      this.$nextTick(() => this.$refs.urlInput && this.$refs.urlInput.focus())
    }
  },
  beforeDestroy() {
    document.removeEventListener('keydown', this.onKeydown)
  },
  methods: {
    onKeydown(e) {
      // Esc closes — but never mid-scan, that would just lose the result.
      if (e.key === 'Escape' && !this.running) this.$emit('close')
    },
    async run() {
      if (this.running || !this.url) return
      if (!this.engines.length) {
        this.error = 'Pick at least one scanner.'
        return
      }
      this.running = true
      this.error = null
      this.ownResult = null
      // A new scan starts from a clean view.
      this.activeTab = 'errors'
      this.search = ''
      this.engineFilter = ''
      this.limit = 25
      try {
        this.ownResult = await runAdaTestScan({
          url: this.url,
          engines: this.engines,
          applyWarnings: this.applyWarnings,
        })
      } catch (e) {
        console.error('test scan failed', e)
        this.error = e.message
      } finally {
        this.running = false
      }
    },
    // Where on the page the finding sits — the engines each name it differently.
    locationOf(item) {
      const raw = item.raw || {}
      const sel = Array.isArray(item.selectors) ? item.selectors.join(', ') : item.selectors
      return item.xpath || item.selector || sel || item.target || raw.xpath || raw.target || ''
    },
    // Per-item breakdown (e.g. every issue inside a failing PDF — TWAC).
    detailsOf(item) {
      const raw = item.raw || {}
      const list = raw.details || raw.issues || []
      if (!Array.isArray(list)) return []
      return list.map(d => (typeof d === 'string' ? d : d.message || d.title || JSON.stringify(d))).slice(0, 20)
    },
  },
}
</script>

<style scoped>
.utm-overlay {
  position: fixed;
  inset: 0;
  z-index: 1080;
  background: rgba(17, 24, 39, 0.55);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem 1rem;
}
.utm-modal {
  background: #fff;
  border-radius: 14px;
  width: 100%;
  max-width: 900px;
  max-height: 88vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  box-shadow: 0 20px 50px rgba(0, 0, 0, 0.25);
}

.utm-header {
  display: flex;
  align-items: center;
  gap: 0.9rem;
  padding: 0.9rem 1.1rem;
  border-bottom: 1px solid #f1f3f5;
}
.utm-header__main {
  flex: 1;
  min-width: 0;
}
.utm-header__title {
  font-weight: 600;
  font-size: 0.95rem;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.utm-header__title a {
  color: #2b303b;
  text-decoration: none;
}
.utm-header__title a:hover {
  text-decoration: underline;
}
.utm-header__meta {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.4rem;
  margin-top: 0.35rem;
  font-size: 11.5px;
  color: #8a919c;
}
.utm-header__time,
.utm-header__note {
  margin-left: 0.35rem;
}
.utm-engine {
  display: inline-flex;
  align-items: center;
  font-size: 10.5px;
  font-weight: 600;
  letter-spacing: 0.03em;
  padding: 0.1rem 0.45rem;
  border-radius: 10px;
}
.utm-engine--ok {
  background: #e7f6ee;
  color: #1a9e55;
}
.utm-engine--fail {
  background: #fdecec;
  color: #d64545;
}
.utm-close {
  border: none;
  background: none;
  color: #adb5bd;
  font-size: 1rem;
  padding: 0.2rem 0.4rem;
}
.utm-close:hover {
  color: #2b303b;
}

.utm-alert,
.utm-note {
  font-size: 12.5px;
  padding: 0.5rem 1.1rem;
  border-bottom: 1px solid #f1f3f5;
}
.utm-alert {
  background: #fdf3e0;
  color: #8a5a00;
}
.utm-note {
  background: #f8f9fb;
  color: #5b6472;
}

.utm-form {
  padding: 0.85rem 1.1rem;
  border-bottom: 1px solid #f1f3f5;
  background: #fafbfc;
}
.utm-form__row {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}
.utm-form__url {
  flex: 1;
  font-size: 0.85rem;
  padding: 0.4rem 0.7rem;
  border: 1px solid #e6e8ec;
  border-radius: 8px;
  background: #fff;
}
.utm-form__url:focus {
  outline: none;
  border-color: #cfd4dc;
}
.utm-form__btn {
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  background: transparent;
  border: 1px solid #e6e8ec;
  border-radius: 7px;
  color: #2b303b;
  font-size: 0.78rem;
  font-weight: 600;
  padding: 0.25rem 0.6rem;
  transition: background 0.12s ease;
}
.utm-form__btn:hover:not(:disabled) {
  background: #f3f5f9;
}
.utm-form__btn:disabled {
  opacity: 0.55;
}
.utm-form__engines {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.85rem;
  margin-top: 0.55rem;
}
.utm-form__engine {
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  font-size: 0.78rem;
  color: #5b6472;
  margin: 0;
  cursor: pointer;
}
.utm-form__engine--sep {
  margin-left: auto;
  color: #8a919c;
}
.utm-form__error {
  margin-top: 0.5rem;
  font-size: 0.78rem;
  color: #d64545;
}

.utm-loading {
  padding: 2.5rem 1.1rem;
  text-align: center;
  color: #5b6472;
  font-size: 13px;
}
.utm-empty--idle {
  padding: 2.5rem 0;
}

.utm-tabs {
  display: flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.5rem 1.1rem;
  border-bottom: 1px solid #f1f3f5;
}
.utm-tab {
  border: none;
  background: none;
  font-size: 0.82rem;
  font-weight: 600;
  color: #8a919c;
  padding: 0.3rem 0.6rem;
  border-radius: 8px;
}
.utm-tab--active {
  background: #f1f3f5;
  color: #2b303b;
}
.utm-tab__badge {
  display: inline-block;
  margin-left: 0.3rem;
  font-size: 11px;
  background: #e9ecef;
  color: #5b6472;
  border-radius: 10px;
  padding: 0 0.4rem;
}
.utm-tabs__right {
  margin-left: auto;
  display: flex;
  align-items: center;
  gap: 0.4rem;
}
.utm-select {
  font-size: 0.75rem;
  padding: 0.2rem 0.4rem;
  border: 1px solid #e6e8ec;
  border-radius: 7px;
  background: transparent;
  color: #5b6472;
}
.utm-search {
  position: relative;
}
.utm-search__icon {
  position: absolute;
  left: 0.5rem;
  top: 50%;
  transform: translateY(-50%);
  font-size: 11px;
  color: #adb5bd;
}
.utm-search__input {
  font-size: 0.75rem;
  padding: 0.25rem 1.5rem 0.25rem 1.5rem;
  border: 1px solid #e6e8ec;
  border-radius: 7px;
  background: transparent;
  width: 200px;
}
.utm-search__input:focus {
  outline: none;
  border-color: #cfd4dc;
}
.utm-search__clear {
  position: absolute;
  right: 0.35rem;
  top: 50%;
  transform: translateY(-50%);
  border: none;
  background: none;
  color: #adb5bd;
  font-size: 11px;
  padding: 0;
}

.utm-body {
  padding: 0.6rem 1.1rem 1.1rem;
  overflow-y: auto;
}
.utm-empty {
  padding: 2rem 0;
  text-align: center;
  color: #8a919c;
  font-size: 13px;
}

.utm-issues {
  list-style: none;
  margin: 0;
  padding: 0;
}
.utm-issue {
  padding: 0.6rem 0.75rem;
  margin-top: 0.5rem;
  border: 1px solid #f1f3f5;
  border-left: 3px solid #e9ecef;
  border-radius: 8px;
  font-size: 13px;
}
.utm-issue--error {
  border-left-color: #d64545;
}
.utm-issue--warn {
  border-left-color: #d9930d;
}
.utm-issue__head {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.4rem;
}
.utm-issue__engine {
  font-size: 10.5px;
  font-weight: 700;
  letter-spacing: 0.03em;
  padding: 0.05rem 0.4rem;
  border-radius: 10px;
  background: #f0f2f5;
  color: #5b6472;
}
.utm-issue__engine--ibm {
  background: #e8f0fe;
  color: #1a56c4;
}
.utm-issue__engine--axe {
  background: #f3e8fd;
  color: #7c3aed;
}
.utm-issue__engine--twac {
  background: #e7f6ee;
  color: #1a9e55;
}
.utm-issue__engine--wave {
  background: #fdf3e0;
  color: #b47a0b;
}
.utm-issue__type {
  font-size: 11px;
  color: #8a919c;
}
.utm-issue__rule {
  font-size: 11.5px;
  color: #2b303b;
  overflow-wrap: anywhere;
}
.utm-issue__help {
  color: #adb5bd;
  font-size: 11px;
}
.utm-issue__help:hover {
  color: #2b303b;
}
.utm-issue__msg {
  margin-top: 0.25rem;
}
.utm-issue__details {
  margin: 0.3rem 0 0;
  padding-left: 1.1rem;
  font-size: 12px;
  color: #5b6472;
}
.utm-issue__loc {
  margin-top: 0.25rem;
}
.utm-issue__loc code {
  font-size: 11px;
  overflow-wrap: anywhere;
}
.utm-issue__contrast {
  margin-top: 0.3rem;
  font-size: 12px;
  color: #5b6472;
}
.utm-swatch {
  display: inline-block;
  width: 11px;
  height: 11px;
  border-radius: 3px;
  border: 1px solid #dee2e6;
  vertical-align: -1px;
  margin: 0 0.2rem;
}

.utm-load-more {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 0.75rem;
  padding: 0.9rem 0 0.25rem;
}
.utm-load-more__btn {
  background: none;
  border: 0;
  padding: 0;
  color: #adb5bd;
  font-size: 0.82rem;
  font-weight: 600;
}
.utm-load-more__btn:hover {
  color: #2b303b;
}
</style>
